import * as React from 'react'
import Navigation from '../../components/Messenger/Navigation'
import styled from 'styled-components'
import ConversationItem from '../../components/Messenger/Conversations/ConversationItem'
import Button from '../../components/Messenger/Button/Button'
import CloseButton from '../../components/Messenger/Navigation/CloseButton'
import { useTranslation } from 'react-i18next'
import ERoute from '../../ERoute'
import { useHistory } from 'react-router-dom'
import Icon from '../../../components/Icons/Icon'
import { Conversation, WidgetSessionChannelEvent, WidgetSessionChannelEventType } from '../../../types'
import Loader from '../../../help_center/components/Loader/Loader'
import ConversationsController from '../../controllers/ConversationsController'
import ActionCableConsumer from '../../../consumers/ActionCableConsumer'
import { useSelector } from 'react-redux'
import { AppState } from '../../store'
import FadeInContainer from '../../components/Messenger/Animation/FadeInContainer'
import ScrollToTopOnMount from '../../effects/ScrollToTopOnMount'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`

const EmptyIcon = styled.div`
  svg {
    width: 32px;
    height: 32px;
    fill: rgba(0, 0, 0, 0.1);
  }
`

const EmptyTitle = styled.div`
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: rgb(28, 28, 28);
  margin-bottom: 8px;
`

const EmptyDescription = styled.div`
  font-weight: 400;
  line-height: 20px;
  font-size: 14px;
  text-align: center;
  color: rgb(28, 28, 28);
  margin-bottom: 24px;
`

const ConversationList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 75px;
`

const Action = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
`

const LoaderContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`

interface IProps { }

interface IState {
  conversations: Conversation[]
  didInitialLoad: boolean
  page: number,
  totalPages: number
}

const Conversations = (props: IProps) => {
  const history = useHistory()
  const { session } = useSelector((state: AppState) => state.application)
  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    conversations: [],
    didInitialLoad: false,
    totalPages: 0,
    page: 1
  })
  const { conversations, didInitialLoad } = state

  const onNewConversationClick = () => {
    history.push(ERoute.PATH_NEW_CONVERSATION)
  }

  const fetchConversations = async () => {
    try {
      const response = await ConversationsController.getConversations()
      const { conversations: newConversations, current_page, total_pages, total_entries } = response;

      setState({
        conversations: [...conversations, ...newConversations],
        didInitialLoad: true,
        page: current_page,
        totalPages: total_pages,
      })

    } catch (ex) {
      console.error(ex)
    }
  }

  React.useEffect(() => {
    fetchConversations().catch(console.error)
  }, [])

  const onActionCableConnected = () => {
    console.log('[Conversations] connected')
  }

  const onActionCableDisconnected = () => {
    console.log('[Conversations] Disconnected')
  }

  const onActionCableReceived = (event: WidgetSessionChannelEvent) => {
    console.log('[Conversations] received event', event)

    switch (event.type) {
      case WidgetSessionChannelEventType.CONVERSATION_CREATE:
        const { conversation } = event.data
        const conversationIndex = conversations.findIndex(c => c.id === conversation.id)

        if (conversationIndex !== -1) return

        setState({ ...state, conversations: [conversation, ...conversations] })
        break
      case WidgetSessionChannelEventType.CONVERSATION_UPDATE:
        const { conversation: updatedConversation } = event.data
        const updateConversationIndex = conversations.findIndex(c => c.id === updatedConversation.id)

        if (updateConversationIndex === -1) {
          setState({ ...state, conversations: [updatedConversation, ...conversations] })
        } else {
          conversations[updateConversationIndex] = updatedConversation
          setState({ ...state, conversations: [...conversations] })
        }
        break
      case WidgetSessionChannelEventType.CONVERSATION_DELETE:
        const { conversation_id } = event.data
        const deleteConversationIndex = conversations.findIndex(c => c.id === conversation_id)

        if (deleteConversationIndex === -1) return

        conversations.splice(deleteConversationIndex, 1)
        setState({ ...state, conversations: [...conversations] })
        break
    }
  }

  return (
    <FadeInContainer>
      <Navigation
        title={t('Messages::Messages')}
        right={<CloseButton />}
      />
      <ScrollToTopOnMount />

      {!didInitialLoad && <LoaderContainer>
        <Loader />
      </LoaderContainer>}

      {didInitialLoad && <ActionCableConsumer
        channel={{ channel: 'WidgetSessionChannel', id: session?.id }}
        onConnected={onActionCableConnected}
        onDisconnected={onActionCableDisconnected}
        onReceived={onActionCableReceived}
      >
        <Container>
          {conversations?.length === 0 && <EmptyContainer>
            <EmptyIcon>
              <Icon icon='messenger-conversations-active' />
            </EmptyIcon>
            <EmptyTitle>{t('Conversations::No messages')}</EmptyTitle>
            <EmptyDescription>{t('Conversations::Messages from the team will be shown here')}</EmptyDescription>

            <Button onClick={onNewConversationClick}>
              {t('Messages::Send us a message')}
              <Icon icon='messenger-send' />
            </Button>
          </EmptyContainer>}

          {conversations?.length > 0 && <>
            <ConversationList>
              {conversations.map((conversation, index) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                />
              ))}
            </ConversationList>
            <Action>
              <Button onClick={onNewConversationClick}>
                {t('Messages::Send us a message')}
                <Icon icon='messenger-send' />
              </Button>
            </Action>
          </>}
        </Container>
      </ActionCableConsumer>}
    </FadeInContainer>
  )
}

export default Conversations